import { useState } from "react";
import BrandingFooter from "../components/BrandingFooter";

interface Props {
  onPair: (code: string, deviceName?: string) => Promise<string | null>;
}

/**
 * Pareo de la terminal — reemplaza el PIN de v1.
 *
 * v2 no tiene PIN: una terminal se empareja con un código que genera el manager en
 * /admin/terminales, y a partir de ahí tiene su propio `device_token` revocable. Eso es lo
 * que hace que el POS sea multi-tenant: el PIN no decía de QUÉ comercio era la caja.
 *
 * El código es de 32 caracteres (`token_urlsafe(24)`) porque está pensado para escanearse
 * por QR desde la app. Acá se pega, así que:
 *   - se acepta el payload completo del QR (`{"server_url":…,"code":…}`) y se extrae el code;
 *   - se acepta `?code=` en la URL, para que el panel pueda ofrecer un enlace directo;
 *   - se avisa que el código dura 90 segundos, que es la causa más común de que falle.
 */
export default function PairingPage({ onPair }: Props) {
  // Deep-link: /?code=… empareja sin que nadie copie nada a mano. Se lee en el inicializador
  // del estado, no en un efecto: un efecto que hace setState dispara un render en cascada.
  const [code, setCode] = useState(
    () => new URLSearchParams(window.location.search).get("code") ?? ""
  );
  const [nombre, setNombre] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (enviando) return;
    setEnviando(true);
    setError(null);
    const msg = await onPair(extraerCodigo(code), nombre.trim() || undefined);
    if (msg) setError(msg);
    setEnviando(false);
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg-primary px-4">
      <form onSubmit={submit} className="w-full max-w-sm">
        <h1 className="mb-1 text-center text-2xl font-bold">Lightning POS</h1>
        <p className="mb-6 text-center text-sm text-text-secondary">
          Conectá esta caja a tu comercio
        </p>

        <label className="mb-1 block text-xs text-text-secondary" htmlFor="code">
          Código de pareo
        </label>
        <input
          id="code"
          data-testid="pairing-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoFocus
          autoComplete="off"
          spellCheck={false}
          placeholder="Pegá el código del panel"
          className="mb-3 w-full rounded-lg border border-border-default bg-bg-surface px-3 py-3 font-mono text-sm outline-none focus:border-accent"
        />

        <label className="mb-1 block text-xs text-text-secondary" htmlFor="nombre">
          Nombre de esta caja <span className="opacity-60">(opcional)</span>
        </label>
        <input
          id="nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          autoComplete="off"
          placeholder="POS web"
          className="mb-4 w-full rounded-lg border border-border-default bg-bg-surface px-3 py-3 text-sm outline-none focus:border-accent"
        />

        {error && (
          <p
            data-testid="pairing-error"
            role="alert"
            className="mb-3 rounded-lg bg-error/15 px-3 py-2 text-sm text-error"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={enviando || !code.trim()}
          className="w-full rounded-lg bg-accent py-3 font-bold text-bg-primary hover:bg-accent-hover disabled:opacity-50"
        >
          {enviando ? "Conectando..." : "Conectar"}
        </button>

        <p className="mt-4 text-center text-xs text-text-secondary">
          Generá el código en <span className="font-mono">Panel → Terminales</span>. Dura 90
          segundos: si vence, generá otro.
        </p>
      </form>

      <a
        href="/demo"
        className="mt-6 text-xs text-text-secondary underline-offset-4 hover:text-text-primary hover:underline"
      >
        Ver demostración
      </a>
      <BrandingFooter />
    </div>
  );
}

/** Acepta el código pelado o el payload del QR pegado entero. */
function extraerCodigo(entrada: string): string {
  const limpio = entrada.trim();
  if (!limpio.startsWith("{")) return limpio;
  try {
    const payload = JSON.parse(limpio);
    return typeof payload?.code === "string" ? payload.code : limpio;
  } catch {
    return limpio;
  }
}
